/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, KeyboardEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionMeta, OnChangeValue } from "react-select";
import {
  LaddaButton,
  ReactSelectSearch,
  OptionTypeBase,
  CriticalResultStatus,
} from "@advapacs/common";
import { useSelector } from "react-redux";
import { IRootState } from "../../config/reducers";
import { useAppDispatch } from "../../config/store";
import { sidebarAction } from "../../reducers/slices/sidebarSlice";
import useCustomerPacsDropdown from "../../hooks/useCustomerPacsDropdown";
import { CriticalResultAction } from "@src/pages/criticalResult/ducks/slices";
import { TabKey } from "@src/pages/criticalResult/CriticalResultTabContainer";

export interface SearchFormProps {
  onSearch: () => void;
  tabKey: TabKey;
}

const CriticalResultFilterForm: FC<SearchFormProps> = ({ onSearch }) => {
  const { t } = useTranslation("form");
  const dispatch = useAppDispatch();

  const { searchState, loading } = useSelector(
    (state: IRootState) => state.criticalResultReducer
  );

  const customers = useCustomerPacsDropdown();
  const { toggleSecondarySidebar } = useSelector(
    (state: IRootState) => state.sidebarReducer
  );

  const { isMobileOnly } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );
  const [preventSearch, setPreventSearch] = useState<boolean>(false);

  const onSearchStateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch(
      CriticalResultAction.setSearchState({ ...searchState, [name]: value })
    );
  };

  const onSearchStateSelectChange = (
    newValue: OnChangeValue<OptionTypeBase, false>,
    actionMeta: ActionMeta<OptionTypeBase>
  ) => {
    if (actionMeta.name) {
      dispatch(
        CriticalResultAction.setSearchState({
          ...searchState,
          [actionMeta.name]: newValue,
        })
      );
    }
  };

  const closeFilterForm = useCallback(() => {
    if (isMobileOnly) {
      dispatch(sidebarAction.toggleSecondarySidebar(!toggleSecondarySidebar));
    }
  }, [dispatch, isMobileOnly, toggleSecondarySidebar]);

  const handleReset = () => {
    setTimeout(() => {
      dispatch(CriticalResultAction.resetSearchState());
    }, 100);
  };

  const onSearchHandle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onSearch();
    closeFilterForm();
  };

  const onSubmitHandler = useCallback(
    (e: KeyboardEvent) => {
      if (preventSearch) {
        return;
      }
      if (e.key === "Enter") {
        dispatch(CriticalResultAction.setSearchState({ searchState }));
        onSearch();
        closeFilterForm();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preventSearch, searchState]
  );

  const useStatusesDropdown = [
    {
      label: t("table:order.new"),
      value: CriticalResultStatus.NEW,
    },
    {
      label: t("viewed"),
      value: CriticalResultStatus.VIEWED,
    },

    {
      label: t("acknowledged"),
      value: CriticalResultStatus.ACKNOWLEDGED,
    },
  ];

  return (
    <>
      <button
        onClick={() => {
          dispatch(
            sidebarAction.toggleSecondarySidebar(!toggleSecondarySidebar)
          );
        }}
        type="button"
        className="btn btn-sidebar-expand sidebar-control sidebar-secondary-toggle"
      >
        <i className="icon-arrow-right13" />
      </button>
      <div className="sidebar-content full-height">
        <form onKeyDown={onSubmitHandler} role="presentation">
          <div className="sidebar-section sidebar-section-body d-flex align-items-center border-bottom pb-1 pt-1">
            <span className="text-uppercase">{t("search")}</span>
            <div className="ml-auto">
              <button
                onClick={() => {
                  dispatch(
                    sidebarAction.toggleSecondarySidebar(
                      !toggleSecondarySidebar
                    )
                  );
                }}
                type="button"
                className="btn btn-outline-light text-body border-transparent btn-icon rounded-pill btn-sm sidebar-control sidebar-secondary-toggle d-none d-lg-inline-flex"
              >
                <i className="icon-transmission" />
              </button>

              <button
                type="button"
                onClick={() => {
                  dispatch(
                    sidebarAction.toggleSecondarySidebar(
                      !toggleSecondarySidebar
                    )
                  );
                }}
                className="btn btn-outline-light text-body border-transparent btn-icon rounded-pill btn-sm sidebar-mobile-secondary-toggle d-lg-none"
              >
                <i className="icon-cross2" />
              </button>
            </div>
          </div>
          <div className="sidebar-section secondary-sidebar left-sidebar">
            <form className="search-form">
              <div className="main-search">
                <div className="card-body">
                  <div className="form-group-tight">
                    <input
                      className="form-control"
                      name="patientName"
                      value={searchState.patientName}
                      onChange={onSearchStateInputChange}
                      placeholder={t("search_form.patient_place_holder")}
                    />
                  </div>
                  <div className="form-group-tight">
                    <input
                      className="form-control"
                      name="patientId"
                      value={searchState.patientId}
                      onChange={onSearchStateInputChange}
                      placeholder={t("search_form.patient_id_place_holder")}
                    />
                  </div>
                  <div className="form-group-tight">
                    <input
                      className="form-control"
                      name="accessionNumber"
                      value={searchState.accessionNumber}
                      onChange={onSearchStateInputChange}
                      placeholder={t(
                        "search_form.accession_number_place_holder"
                      )}
                    />
                  </div>
                  <div className="form-group-tight">
                    <div className="full-width">
                      <ReactSelectSearch
                        handleFocus={() => setPreventSearch(true)}
                        handleBlur={() => setPreventSearch(false)}
                        name="customers"
                        placeholder={t("all_pacs")}
                        options={customers}
                        handleSelectChange={onSearchStateSelectChange}
                        value={searchState.customers}
                        isMulti
                        isSearchable
                      />
                    </div>
                  </div>
                  <div className="form-group-tight">
                    <div className="full-width">
                      <ReactSelectSearch
                        handleFocus={() => setPreventSearch(true)}
                        handleBlur={() => setPreventSearch(false)}
                        name="statuses"
                        placeholder={t("all_statuses")}
                        options={useStatusesDropdown}
                        handleSelectChange={onSearchStateSelectChange}
                        value={searchState.statuses}
                        isMulti
                        isSearchable
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className="search-form-btn p-sm">
            <LaddaButton
              btnName={t("reset")}
              type="button"
              onClickHandler={(e) => {
                e.preventDefault();
                handleReset();
              }}
              disabled={false}
              className="btn btn-light full-width"
              loading={false}
            />
            <LaddaButton
              btnName={t("search")}
              type="button"
              onClickHandler={onSearchHandle}
              disabled={loading}
              className="btn btn-primary full-width"
              loading={loading}
            />
          </div>
        </form>
      </div>
    </>
  );
};

// @ts-ignore
export default CriticalResultFilterForm;
